/**
 * 统一的认证错误处理工具
 * 处理401错误、token刷新、用户登出等认证相关逻辑
 */

// 使用相对路径，通过Nginx代理访问后端API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 防止重复刷新token的锁 & 失败后的降噪标记
let isRefreshing = false;
let refreshPromise = null;
let refreshDisabled = false; // 一旦检测到后端返回401（无refresh_token等），当前页面生命周期内不再尝试刷新

/**
 * 尝试刷新access token
 * @returns {Promise<boolean>} 刷新是否成功
 */
export async function tryRefreshToken() {
  // 如果当前页面已经确认无法刷新（例如后端返回过一次401），直接短路，避免重复打后端
  if (refreshDisabled) {
    return false;
  }
  // 如果正在刷新，等待当前的刷新完成
  if (isRefreshing && refreshPromise) {
    try {
      await refreshPromise;
      return true;
    } catch (error) {
      return false;
    }
  }

  // 开始刷新
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // 重要：包含cookie中的refresh_token
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          refreshDisabled = true;
          return false;
        }
        throw new Error(`刷新令牌失败: ${response.status}`);
      }

      const data = await response.json();
      
      if (data?.success && data?.access_token) {
        localStorage.setItem('access_token', data.access_token);
        return true;
      }

      return false;
    } catch (error) {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  try {
    return await refreshPromise;
  } catch (error) {
    return false;
  }
}

/**
 * 处理认证错误
 * 清除用户信息并跳转到登录页（不再弹出“登录已过期”提示框，直接跳转更简洁）
 * @param {string} message - 错误消息（仅用于日志）
 * @param {boolean} showAlert - 保留兼容参数（目前不再使用弹窗）
 * @param {boolean} redirectToLogin - 是否跳转到登录页
 */
export async function handleAuthError(message = '登录已过期，请重新登录', showAlert = false, redirectToLogin = true) {
  localStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('token');
  
  // 清除其他相关状态
  localStorage.removeItem('searchLastVisit');
  localStorage.removeItem('currentSessionId');
  localStorage.removeItem('searchLastUserId');
  localStorage.removeItem('chatSessions');
  localStorage.removeItem('jobSearchResults');
  localStorage.removeItem('currentSearchQuery');
  localStorage.removeItem('searchResults');
  localStorage.removeItem('searchQuery');

  // 直接跳转到用户登录主页（例如 http://localhost:3000/ 的首页）
  // 避免弹窗，用户体验更干净
  if (redirectToLogin && !window.location.pathname.startsWith('/admin')) {
    setTimeout(() => {
      window.location.href = '/';
    }, 50);
  }
}

/**
 * 检查响应并处理认证错误
 * @param {Response} response - fetch响应对象
 * @param {boolean} showAlert - 是否显示alert
 * @returns {boolean} 是否是认证错误
 */
export async function checkAndHandleAuthError(response, showAlert = false) {
  if (response.status === 401) {
    // 尝试刷新token
    const refreshSuccess = await tryRefreshToken();
    
    if (!refreshSuccess) {
      // 刷新失败，处理认证错误
      await handleAuthError('登录已过期，请重新登录', showAlert, true);
      return true;
    }
    
    // 刷新成功，返回false表示不是致命错误，可以重试请求
    return false;
  }
  
  return false;
}

/**
 * 处理Axios的401错误
 * 自动尝试刷新token并重试原始请求
 * @param {Error} error - Axios错误对象
 * @returns {Promise} 重试后的响应或拒绝的Promise
 */
export async function handleAxiosAuthError(error) {
  // 检查是否是401错误
  if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  // 检查原始请求配置
  const originalRequest = error.config;

  // 如果已经重试过，直接处理认证错误
  if (originalRequest._retry) {
    await handleAuthError('登录已过期，请重新登录', false, true);
    return Promise.reject(error);
  }

  // 标记为已重试
  originalRequest._retry = true;

  // 尝试刷新token
  const refreshSuccess = await tryRefreshToken();

  if (!refreshSuccess) {
    // 刷新失败，处理认证错误
    await handleAuthError('登录已过期，请重新登录', false, true);
    return Promise.reject(error);
  }

  // 刷新成功，更新请求头中的token并重试
  const newToken = localStorage.getItem('access_token');
  if (newToken && originalRequest.headers) {
    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
  }

  // 重试原始请求
  try {
    const axios = (await import('axios')).default;
    return await axios(originalRequest);
  } catch (retryError) {
    // 重试仍然失败，检查是否是401错误
    if (retryError.response?.status === 401) {
      await handleAuthError('登录已过期，请重新登录', false, true);
    }
    return Promise.reject(retryError);
  }
}

